'use client';
/**
 * Lot N5 — le référentiel des emplacements, vu des écrans qui le CONSOMMENT.
 *
 * Trois écrans en ont besoin pour la même chose : proposer une liste d'endroits
 * et savoir écrire le chemin lisible d'un code (« Pré-analytique › A2 › E1 »).
 * Le code seul ne dit rien à personne — c'est le chemin qu'on lit, et c'est lui
 * qu'il faut afficher sur une fiche d'équipement comme sur une copie maîtrisée.
 *
 * Chargé ici plutôt que dans le magasin global : le référentiel ne sert qu'à
 * une poignée d'écrans, et le tirer à chaque rafraîchissement de la Qualité
 * coûterait une lecture à tout le monde. Même raisonnement que `useEtalons`
 * dans la fiche d'équipement.
 */
import React from 'react';

export type EmplacementRef = {
  documentId: string; code: string; libelle: string;
  niveau: string; parentCode: string; actif: boolean;
};

/** `config/api.ts` plafonne à 100 lignes EN SILENCE. Le référentiel en compte
 *  33 aujourd'hui — il en comptera plus le jour où le laboratoire décrira ses
 *  paillasses, et une liste tronquée ne se verrait pas : on pagine. */
async function toutes(): Promise<EmplacementRef[]> {
  const out: EmplacementRef[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const r = await fetch(
      `/api/ged/ged-emplacements?sort=ordre:asc&pagination[page]=${page}&pagination[pageSize]=100`,
      { credentials: 'include' },
    ).catch(() => null);
    if (!r || !r.ok) break;
    const j = await r.json().catch(() => null);
    const data: EmplacementRef[] = j?.data ?? [];
    out.push(...data);
    if (!data.length || page >= (j?.meta?.pagination?.pageCount ?? 1)) break;
  }
  return out;
}

export function useEmplacements() {
  const [liste, setListe] = React.useState<EmplacementRef[] | null>(null);
  React.useEffect(() => {
    let vivant = true;
    void toutes().then((d) => { if (vivant) setListe(d); });
    return () => { vivant = false; };
  }, []);

  const index = React.useMemo(
    () => new Map((liste || []).map((e) => [e.code, e])),
    [liste],
  );

  /** Le chemin lisible d'un code. Un code ABSENT du référentiel n'est pas
   *  effacé ni remplacé par « — » : il est rendu tel quel, suivi d'une mention.
   *  C'est le seul moyen de voir qu'une fiche pointe sur un emplacement qui
   *  n'existe plus — un rattachement effacé en silence, personne ne le
   *  remarquerait. */
  const chemin = React.useCallback((code: string): string => {
    const c = String(code || '').trim();
    if (!c) return '';
    if (!index.has(c)) return liste === null ? c : `${c} (emplacement inconnu)`;
    const parts: string[] = [];
    let courant = index.get(c);
    for (let garde = 0; courant && garde <= index.size; garde += 1) {
      parts.unshift(courant.libelle || courant.code);
      const p = courant.parentCode;
      if (!p || p === courant.code) break;
      courant = index.get(p);
    }
    return parts.join(' › ');
  }, [index, liste]);

  /** Ce qu'on propose dans un sélecteur : les emplacements EN SERVICE, triés
   *  par leur chemin — c'est l'ordre du plan, pas celui des codes. */
  const proposables = React.useMemo(
    () => (liste || [])
      .filter((e) => e.actif !== false)
      .map((e) => ({ code: e.code, chemin: chemin(e.code) }))
      .sort((a, b) => a.chemin.localeCompare(b.chemin, 'fr')),
    [liste, chemin],
  );

  return { emplacements: liste, index, chemin, proposables, charge: liste !== null };
}
